import { MAX_PRESENTATION_UPLOAD_BYTES } from "@/config/limits";

export type PresentationSourceFormat = "pptx" | "pdf";

export const PRESENTATION_SOURCE_FORMATS: PresentationSourceFormat[] = ["pptx", "pdf"];

export const PPTX_MIME =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
export const PDF_MIME = "application/pdf";

export const PRESENTATION_UPLOAD_ACCEPT = `.pptx,.pdf,${PPTX_MIME},${PDF_MIME}`;

export const PRESENTATION_UPLOAD_HINT = "PowerPoint (.pptx) or PDF, up to 50MB";

export const PRESENTATION_UPLOAD_NOTE =
  "PDFs exported from Keynote or Google Slides work too. Charts in PDFs are read from the image.";

export const UNSUPPORTED_PRESENTATION_MESSAGE =
  "Unsupported file type. Upload a PowerPoint (.pptx) or PDF file.";

export const SOURCE_FORMAT_LABELS: Record<PresentationSourceFormat, string> = {
  pptx: "PowerPoint",
  pdf: "PDF",
};

const GENERIC_MIMES = new Set(["", "application/octet-stream", "binary/octet-stream"]);

const ALLOWED_MIMES: Record<PresentationSourceFormat, Set<string>> = {
  pptx: new Set([PPTX_MIME, "application/zip", "application/x-zip-compressed", ...GENERIC_MIMES]),
  pdf: new Set([PDF_MIME, "application/x-pdf", ...GENERIC_MIMES]),
};

export function sourceFormatFromFilename(
  name: string | null | undefined,
): PresentationSourceFormat | null {
  const ext = (name ?? "").split("?")[0].trim().split(".").pop()?.toLowerCase();
  return ext === "pptx" || ext === "pdf" ? ext : null;
}

export function presentationSourceFormat(
  presentation:
    | { source_format?: string | null; original_filename?: string | null }
    | null
    | undefined,
): PresentationSourceFormat | null {
  const stored = presentation?.source_format;
  if (stored === "pptx" || stored === "pdf") return stored;
  return sourceFormatFromFilename(presentation?.original_filename);
}

function hasPdfSignature(bytes: Uint8Array): boolean {
  const signature = [0x25, 0x50, 0x44, 0x46, 0x2d];
  for (let i = 0; i + signature.length <= bytes.length; i++) {
    if (signature.every((b, j) => bytes[i + j] === b)) return true;
  }
  return false;
}

function hasZipSignature(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 4 &&
    bytes[0] === 0x50 &&
    bytes[1] === 0x4b &&
    bytes[2] === 0x03 &&
    bytes[3] === 0x04
  );
}

export async function validatePresentationFile(
  file: File,
): Promise<{ format: PresentationSourceFormat } | { error: string }> {
  const format = sourceFormatFromFilename(file.name);
  if (!format) return { error: UNSUPPORTED_PRESENTATION_MESSAGE };
  if (file.size === 0) return { error: "The file is empty." };
  if (file.size > MAX_PRESENTATION_UPLOAD_BYTES) {
    return { error: "The file is larger than 50MB." };
  }
  const mime = (file.type || "").split(";")[0].trim().toLowerCase();
  if (!ALLOWED_MIMES[format].has(mime)) {
    return { error: UNSUPPORTED_PRESENTATION_MESSAGE };
  }
  const head = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
  const matches = format === "pdf" ? !hasZipSignature(head) && hasPdfSignature(head) : hasZipSignature(head);
  if (!matches) {
    const label = format === "pdf" ? "PDF" : "PowerPoint (.pptx)";
    return {
      error: `This file is not a valid ${label} file. It may be damaged, password-protected, or saved in a different format.`,
    };
  }
  return { format };
}
